/**
 * The reach ledger — the one honest comparison across surfaces.
 *
 * WHY REACH, AND NOT PERFORMANCE
 * -----------------------------------------------------------------------------
 * The question the owner asked is *which channels are working for me, what is
 * the gain, what is the loss, which ones are better*. The obvious answer is a
 * leaderboard of per-channel performance, and it is the wrong one: email's
 * delivered, WhatsApp's sent and an ad set's impressions are three different
 * populations, so any percentage computed across them is this project's worst
 * shipped defect wearing a new name — a rate whose denominator spans
 * channels.
 *
 * `surface.ts` therefore defines no ratio at all, and this file compares the
 * one thing the surfaces that address this workspace's own contacts have in
 * common: **people**. Counted by `person_id` since slice 58 — the stored person
 * this product decides two rows are one human by (until then, `coalesce(email,
 * phone_e164)`).
 *
 * That scope is the ledger's, not an accident of what was built first. An ad
 * surface reaches people too, but they are Meta's audience, counted by Meta
 * and never matched against these contacts — so an ad row here would set
 * Meta's count beside a count of this list, two populations in one column.
 * Slice 44 built the ad surfaces, and the ledger gates on `kind === 'direct'`
 * as well as on built for exactly that reason.
 *
 * Reach is also the one figure here that is real *before* a provider is
 * connected: the addresses are in this database, so "how many of our people
 * could email, or WhatsApp, reach" is always answerable.
 *
 * WHY EXCLUSIVE REACH IS THE FIGURE THAT DECIDES ANYTHING
 * -----------------------------------------------------------------------------
 * "WhatsApp reaches 4,100" is not actionable on its own — if 4,000 of those are
 * also emailable, dropping WhatsApp costs a hundred people. `exclusive` is how
 * many a surface is the **only** way to reach, and it is the number that answers
 * "is this channel worth keeping".
 *
 * **Counted directly, never by subtraction.** `reachable − overlap` is an
 * arithmetic identity that holds only while every term was counted over the
 * same population at the same instant; the moment one of them is filtered
 * differently the difference goes quietly wrong and stays plausible. Every
 * figure here is its own `count(*)` over one grouped table in one statement.
 *
 * WHAT THIS DELIBERATELY CANNOT SAY
 * -----------------------------------------------------------------------------
 * No rate, no score, no ranking. A ledger that sorted surfaces by a computed
 * "effectiveness" would be inventing the comparison the whole design refuses.
 * The rows are ordered by the taxonomy, and a reader draws their own conclusion
 * from two counts they can check.
 *
 * Spend against outcome is the other honest comparison and it is NOT here.
 * It is slice 45's `surface-comparison.ts`, over a period rather than at an
 * instant, and its ad reach is Meta's rather than this list's — so it is not
 * a second column of this shape. Its one ratio, spend per 1,000 reached,
 * stays inside one row: one placement's money over the same placement's
 * people, never a rate across surfaces.
 */

import { z } from 'zod';

import { surfaceSchema } from './surface.js';

/**
 * One surface's standing with this workspace's own people.
 *
 * Both figures are counts of **people**, never of contact rows. `contacts` is
 * one row per (person, upload) by design, so a `count(*)` over it is an upload
 * count wearing the word "people" — the defect slice 32 shipped and fixed.
 */
export const reachRowSchema = z
  .object({
    surface: surfaceSchema,
    /**
     * People this surface could reach today: they have the address it needs and
     * they have not asked this workspace to stop on that channel.
     *
     * Zero is a real zero here and means what it says — nobody on this list can
     * be reached this way. It is not a missing measurement: the addresses are
     * in this database, so the question is always answerable.
     */
    reachable: z.number().int().min(0),
    /**
     * People **only** this surface can reach. Always `<= reachable`, and the
     * two are counted over one grouped table so that holds by construction.
     */
    exclusive: z.number().int().min(0),
  })
  .strict();
export type ReachRow = z.infer<typeof reachRowSchema>;

export const reachLedgerSchema = z
  .object({
    generatedAt: z.string().datetime(),
    /**
     * Distinct people on this workspace's lists, suppressed or not.
     *
     * The denominator a row's bar is drawn against, and the only figure here
     * that is not per surface. Suppressed people are counted: they are on the
     * list, and a surface that cannot reach them is exactly what the row is
     * reporting.
     */
    people: z.number().int().min(0),
    /**
     * One row per built surface that addresses contacts — `SURFACE_BUILT` and
     * `kind === 'direct'`, in taxonomy order. A surface with no adapter has no
     * row and draws nothing, because a placeholder for an unbuilt channel is a
     * promise on a dashboard; an ad surface has none either, because its reach
     * is Meta's audience, not people on this workspace's lists.
     */
    rows: z.array(reachRowSchema),
  })
  .strict();
export type ReachLedger = z.infer<typeof reachLedgerSchema>;
